import { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { FaPlus, FaTrophy, FaHistory, FaUserPlus, FaRandom, FaFilter, FaSearch, FaUsers, FaGamepad, FaExternalLinkAlt, FaCode, FaBellSlash, FaBell, FaEdit, FaTrash, FaShare, FaLink, FaTimes, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { useFriends } from '../contexts/FriendContext';
import api from '../utils/api';
import NotificationService from '../services/notificationService';
import { Input } from '../components/ui';
import { ChallengeListSkeleton } from '../components/skeletons';
import { io } from 'socket.io-client';
import config from '../config/env';

// Lazy load heavy components
const CollapsibleChat = lazy(() => import('../components/chat/CollapsibleChat'));
const CreateChallengeModal = lazy(() => import('../components/challenges/CreateChallengeModal'));

const Challenges = () => {
  const { currentUser } = useAuth();
  const { searchUsers, sendFriendRequest } = useFriends();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [hasMore, setHasMore] = useState(true);
  const [showCreateChallengeModal, setShowCreateChallengeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Show All'); // 'all', 'active', 'upcoming', 'completed' - default to 'active'
  const [sortOrder, setSortOrder] = useState('newest');
  const [activeTab] = useState('all'); // 'all', 'my', 'joined'
  const [topUsers, setTopUsers] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [challengeToDelete, setChallengeToDelete] = useState(null);
  const [remindersSet, setRemindersSet] = useState({});
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState([]);
  const [problemCountFilter, setProblemCountFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showFriendsConnectModal, setShowFriendsConnectModal] = useState(false);

  // Refs for infinite scroll
  const scrollContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const prevFiltersRef = useRef({ searchTerm: '', statusFilter: 'active', sortOrder: 'newest', difficultyFilter: 'all', tagFilter: [], problemCountFilter: 'all' });
  const socketRef = useRef(null);

  // Initialize socket connection for real-time notifications
  useEffect(() => {
    socketRef.current = io(config.apiUrl);
    
    // Listen for challenge reminders
    socketRef.current.on('challenge-reminder', (data) => {
      
      toast.info(data.message, {
        position: 'top-right',
        duration: 10000,
        className: 'challenge-reminder-toast'
      });
    });
    
    return () => {
      if (socketRef.current) {
        socketRef.current.off('challenge-reminder');
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Load reminders from localStorage on mount
  useEffect(() => {
    try {
      const savedReminders = localStorage.getItem('challengeReminders');
      if (savedReminders) {
        setRemindersSet(JSON.parse(savedReminders));
      }
    } catch (error) {
      
    }
  }, []);

  // DON'T clear reminders on unmount - keep them persistent
  // Reminders should persist across page navigation and refresh

  // Sync hasMore to ref for scroll handler
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Set tab title and favicon for Challenges
  useEffect(() => {
    document.title = 'CodeBattles - Coding Challenge | Challenges';
    const link = document.querySelector("link[rel='icon']");
    if (link) { link.setAttribute('href', '/codebattles-logo-small.png'); }
  }, []);

  // Fetch challenges with infinite scroll
  useEffect(() => {
    /**
    * Synchronizes challenge list state by fetching data with current filters, handling load-more logic, and updating pagination state.
    * @example
    * sync()
    * undefined
    * @returns {Promise<void>} Resolves after updating challenge data and related loading indicators.
    **/
    const fetchChallenges = async () => {
      const isLoadMore = isLoadingMoreRef.current;

      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setChallenges([]);
        }

        const response = await api.get('/api/challenges', {
          params: { 
            page, 
            limit, 
            status: statusFilter !== 'all' ? statusFilter : undefined, 
            sort: sortOrder, 
            search: searchTerm || undefined,
            difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined
          }
        });
        
        const fetchedChallenges = response.data.challenges || [];
        
        // Extract unique tags from all challenges
        const allTags = fetchedChallenges.reduce((acc, challenge) => {
          if (challenge.tags && Array.isArray(challenge.tags)) {
            return [...acc, ...challenge.tags];
          }
          return acc;
        }, []);
        
        // Get unique tags
        const uniqueTags = [...new Set(allTags)].sort();
        setAvailableTags(uniqueTags);

        if (isLoadMore) {
          setChallenges(prev => {
            const newChallenges = [...prev, ...fetchedChallenges];
            
            return newChallenges;
          });
        } else {
          setChallenges(fetchedChallenges);
          
        }

        const totalPgs = response.data.totalPages || 1;
        setHasMore(page < totalPgs);

        // Prefetch next page if available
        if (page < totalPgs && !isLoadMore) {
          api.get('/api/challenges', {
            params: { 
              page: page + 1, 
              limit, 
              status: statusFilter !== 'all' ? statusFilter : undefined, 
              sort: sortOrder, 
              search: searchTerm || undefined,
              difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined
            }
          }).catch(() => {}); // Silently fail prefetch
        }

        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      } catch (error) {
        
        setLoading(false);
        setLoadingMore(false);
        if (!isLoadMore) {
          setChallenges([]);
        }
      } finally {
        isLoadingMoreRef.current = false;
      }
    };

    // Check if filters changed (not just page)
    const filtersChanged = 
      prevFiltersRef.current.searchTerm !== searchTerm ||
      prevFiltersRef.current.statusFilter !== statusFilter ||
      prevFiltersRef.current.sortOrder !== sortOrder ||
      prevFiltersRef.current.difficultyFilter !== difficultyFilter ||
      prevFiltersRef.current.tagFilter.join(',') !== tagFilter.join(',') ||
      prevFiltersRef.current.problemCountFilter !== problemCountFilter;

    if (filtersChanged && page !== 1) {
      // Filters changed, reset to page 1
      
      prevFiltersRef.current = { searchTerm, statusFilter, sortOrder, difficultyFilter, tagFilter, problemCountFilter };
      isLoadingMoreRef.current = false;
      setPage(1);
      setChallenges([]);
      setHasMore(true);
      return;
    }

    // Update prev filters
    prevFiltersRef.current = { searchTerm, statusFilter, sortOrder, difficultyFilter, tagFilter, problemCountFilter };

    fetchChallenges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, sortOrder, searchTerm, difficultyFilter, tagFilter.join(','), problemCountFilter]);

  // Infinite scroll handler
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      // Trigger when within 200px of bottom
      if (distanceFromBottom < 200 && hasMoreRef.current && !isLoadingMoreRef.current) {
        
        isLoadingMoreRef.current = true;
        setPage(prev => prev + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch top users (cached, only fetch once)
  useEffect(() => {
    /**
    * Synchronizes the cached leaderboard with the latest top users from the API.
    * @example
    * sync()
    * undefined
    * @param {{void}} {{none}} - No parameters.
    * @returns {{Promise<void>}} Promise that resolves once the cache and state are updated.
    **/
    const fetchTopUsers = async () => {
      try {
        // Check session cache first
        const cached = sessionStorage.getItem('topUsers');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed && Date.now() - parsed.time < 5 * 60 * 1000) { // 5 min cache
              setTopUsers(parsed.data || []);
              return;
            }
          } catch {}
        }
        
        const response = await api.get('/api/leaderboard/global?limit=5');
        const data = response.data.leaderboard || [];
        setTopUsers(data);
        sessionStorage.setItem('topUsers', JSON.stringify({ time: Date.now(), data }));
      } catch (error) {
        
      }
    };

    fetchTopUsers();
  }, []);

  // Friends are now fetched from FriendContext

  const handleChallengeCreated = useCallback((newChallenge) => {
    setChallenges(prev => [newChallenge, ...prev]);
    toast.success('Challenge created successfully!', { position: 'top-right', className: 'small-toast' });
    setShowCreateChallengeModal(false);
  }, []);
  
  const handleEditChallenge = useCallback((challenge) => {
    // Navigate to challenge edit page with the challenge data
    navigate(`/challenges/${challenge._id}/edit`);
  }, [navigate]);
  
  const handleDeleteChallenge = useCallback((challenge) => {
    setChallengeToDelete(challenge);
    setShowDeleteModal(true);
  }, []);
  
  const confirmDeleteChallenge = useCallback(async () => {
    if (!challengeToDelete) return;
    
    try {
      await api.delete(`/api/challenges/${challengeToDelete._id}`);
      setChallenges(prev => prev.filter(c => c._id !== challengeToDelete._id));
      toast.success('Challenge deleted successfully!', { position: 'top-right', className: 'small-toast' });
      setShowDeleteModal(false);
      setChallengeToDelete(null);
    } catch (error) {
      
      toast.error('Failed to delete challenge', { position: 'top-right', className: 'small-toast' });
    }
  }, [challengeToDelete]);
  
  const handleSetReminder = useCallback(async (challenge) => {
    try {
      await NotificationService.setChallengeReminder(challenge);
      const newReminders = { ...remindersSet, [challenge._id]: true };
      setRemindersSet(newReminders);
      
      // Save to localStorage
      try {
        localStorage.setItem('challengeReminders', JSON.stringify(newReminders));
      } catch (error) {
        
      }
      
      toast.success('Reminder set! You will be notified 5 minutes before the challenge starts.', { 
        position: 'top-right', 
        className: 'small-toast' 
      });
    } catch (error) {
      
      toast.error('Failed to set reminder', { position: 'top-right', className: 'small-toast' });
    }
  }, [remindersSet]);

  const handleHistory = useCallback(() => {
    navigate('/history');
  }, [navigate]);

  const handleRandomChallenge = useCallback(() => {
    if (!challenges || challenges.length === 0) {
      toast.error('No challenges available!', { position: 'top-right', className: 'small-toast' });
      return;
    }
    const randomIdx = Math.floor(Math.random() * challenges.length);
    const randomChallenge = challenges[randomIdx];
    navigate(`/challenges/${randomChallenge._id}`);
  }, [challenges, navigate]);

  const handleInviteFriend = useCallback((challenge) => {
    setSelectedChallenge(challenge);
    setShowInviteModal(true);
  }, []);
  
  const handleOpenFriendsConnect = useCallback((challenge) => {
    setSelectedChallenge(challenge);
    setShowFriendsConnectModal(true);
  }, []);
  
  const generateInviteLink = useCallback((challengeId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/challenges/${challengeId}`;
  }, []);

  const handleSearchUsers = useCallback(async () => {
    if (!usernameSearch.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      setSearchLoading(true);
      const results = await searchUsers(usernameSearch);
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      
      toast.error('Failed to search users', { position: 'top-right', className: 'small-toast' });
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [usernameSearch, searchUsers]);
  
  const handleInviteUser = useCallback(async (userId, isFriend = false) => {
    try {
      if (isFriend) {
        // If the user is already a friend, send a challenge invite
        await api.post('/api/challenges/invite-user', {
          challengeId: selectedChallenge?._id,
          userId: userId
        });
        toast.success('Challenge invitation sent successfully!', { position: 'top-right', className: 'small-toast' });
      } else {
        // If the user is not a friend, send a friend request first
        const success = await sendFriendRequest(userId);
        if (success) {
          toast.info('Friend request sent! You can invite them to challenges once they accept.', { position: 'top-right', className: 'small-toast' });
        }
      }
    } catch (error) {
      
      toast.error('Failed to send invitation', { position: 'top-right', className: 'small-toast' });
    }
  }, [selectedChallenge, sendFriendRequest]);

  const copyInviteLink = useCallback(() => {
    navigator.clipboard.writeText(generateInviteLink(selectedChallenge?._id))
      .then(() => {
        toast.success('Invite link copied to clipboard!', { position: 'top-right', className: 'small-toast' });
      })
      .catch((error) => {
        
        toast.error('Failed to copy link', { position: 'top-right', className: 'small-toast' });
      });
  }, [selectedChallenge, generateInviteLink]);
  
  const toggleTagFilter = useCallback((tag) => {
    setTagFilter(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  }, []);

  const handleShowCreateModal = useCallback(() => {
    setShowCreateChallengeModal(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateChallengeModal(false);
  }, []);

  const handleInviteFriendsClick = useCallback(() => {
    if (challenges && challenges.length > 0) {
      const challenge = selectedChallenge || challenges[0];
      handleOpenFriendsConnect(challenge);
    } else {
      toast.error('No challenges available to invite friends to!', { position: 'top-right', className: 'small-toast' });
    }
  }, [challenges, selectedChallenge, handleOpenFriendsConnect]);

  const handleCloseInviteModal = useCallback(() => {
    setShowInviteModal(false);
  }, []);

  const handleCloseFriendsModal = useCallback(() => {
    setShowFriendsConnectModal(false);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setChallengeToDelete(null);
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    setUsernameSearch(e.target.value);
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearchUsers();
    }
  }, [handleSearchUsers]);

  const handleSearchTermChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSortChange = useCallback((e) => {
    setSortOrder(e.target.value);
  }, []);

  const handleDifficultyChange = useCallback((e) => {
    setDifficultyFilter(e.target.value);
  }, []);

  const handleProblemCountChange = useCallback((e) => {
    setProblemCountFilter(e.target.value);
  }, []);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const handleClearFilters = useCallback(() => {
    setDifficultyFilter('all');
    setProblemCountFilter('all');
    setTagFilter([]);
  }, []);

  const handleStatusFilterActive = useCallback(() => {
    setStatusFilter('active');
    setPage(1);
    setChallenges([]);
    setHasMore(true);
    isLoadingMoreRef.current = false;
  }, []);

  const handleStatusFilterUpcoming = useCallback(() => {
    setStatusFilter('upcoming');
    setPage(1);
    setChallenges([]);
    setHasMore(true);
    isLoadingMoreRef.current = false;
  }, []);

  const handleStatusFilterCompleted = useCallback(() => {
    setStatusFilter('completed');
    setPage(1);
    setChallenges([]);
    setHasMore(true);
    isLoadingMoreRef.current = false;
  }, []);

  const handleStatusFilterAll = useCallback(() => {
    setStatusFilter('all');
    setPage(1);
    setChallenges([]);
    setHasMore(true);
    isLoadingMoreRef.current = false;
  }, []);

  const handleChallengeClick = useCallback((challengeId) => {
    navigate(`/challenges/${challengeId}`);
  }, [navigate]);

  const handleReminderClick = useCallback((e, challenge) => {
    e.stopPropagation();
    handleSetReminder(challenge);
  }, [handleSetReminder]);

  const handleEditClick = useCallback((e, challenge) => {
    e.stopPropagation();
    handleEditChallenge(challenge);
  }, [handleEditChallenge]);

  const handleDeleteClick = useCallback((e, challenge) => {
    e.stopPropagation();
    handleDeleteChallenge(challenge);
  }, [handleDeleteChallenge]);

  const handleInviteClick = useCallback((e, challenge) => {
    e.stopPropagation();
    handleInviteFriend(challenge);
  }, [handleInviteFriend]);

  const handleCopyLinkClick = useCallback((e) => {
    e.stopPropagation();
    copyInviteLink();
  }, [copyInviteLink]);

  const handleShareClick = useCallback((e, challenge) => {
    e.stopPropagation();
    navigator.share({
      title: `CodeBattle Challenge: ${challenge.title}`,
      text: challenge.description || 'Join this coding challenge!',
      url: `${window.location.origin}/challenges/${challenge._id}`
    }).catch(err => {
      // Share failed - silently handle
    });
  }, []);

  const handleUsernameSearchChange = useCallback((e) => {
    setUsernameSearch(e.target.value);
  }, []);

  const handleUsernameSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearchUsers();
    }
  }, [handleSearchUsers]);

  const handleCloseInviteModalClick = useCallback(() => {
    setShowInviteModal(false);
  }, []);

  const handleCloseFriendsModalClick = useCallback(() => {
    setShowFriendsConnectModal(false);
  }, []);

  const handleCloseDeleteModalClick = useCallback(() => {
    setShowDeleteModal(false);
    setChallengeToDelete(null);
  }, []);

  // Check if leaderboard tab is active
  if (activeTab === 'leaderboard') {
    return (
      <div className="bg-background-primary dark:bg-background-tertiary rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <FaTrophy className="mr-2 text-yellow-500" /> Global Leaderboard
          </h2>
          <div className="text-sm text-text-secondary">
            Top performers across all challenges
          </div>
        </div>
        
        {topUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FaTrophy className="text-4xl text-text-tertiary mb-4 opacity-30" />
            <div className="text-text-tertiary font-medium mb-2">No leaderboard data available yet</div>
            <div className="text-text-tertiary text-sm">Be the first to participate in challenges!</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border-primary rounded-lg overflow-hidden border border-border-primary">
              <thead className="bg-background-secondary dark:bg-background-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Problems Solved</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Challenges Won</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Success Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary bg-background-primary dark:bg-background-tertiary">
                {topUsers.map((user, idx) => {
                  const isCurrentUser = currentUser && (user.userId === currentUser._id || user.user?._id === currentUser._id);
                  return (
                    <tr 
                      key={idx} 
                      className={`${isCurrentUser ? 'bg-blue-50 dark:bg-blue-900/20' : idx % 2 === 0 ? 'bg-background-primary dark:bg-background-tertiary' : 'bg-background-secondary dark:bg-background-secondary'}`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-text-primary">
                        {idx === 0 ? (
                          <span className="inline-flex items-center justify-center size-6 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 rounded-full">🥇</span>
                        ) : idx === 1 ? (
                          <span className="inline-flex items-center justify-center size-6 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 rounded-full">🥈</span>
                        ) : idx === 2 ? (
                          <span className="inline-flex items-center justify-center size-6 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 rounded-full">🥉</span>
                        ) : (
                          <span className="text-text-secondary">{idx + 1}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        <div className="flex items-center">
                          <div className="size-8 rounded-full bg-background-tertiary flex items-center justify-center text-text-primary mr-2">
                            {(user.username || user.user?.username || 'A').charAt(0).toUpperCase()}
                          </div>
                          <span className={isCurrentUser ? 'font-medium' : ''}>
                            {user.username || user.user?.username || 'Anonymous'}
                            {isCurrentUser && <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(You)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">{user.problemsSolved || 0}</td>
                      <td className="px-4 py-3 text-sm text-text-primary">{user.challengesWon || 0}</td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mr-2">
                            <div 
                              className="bg-green-500 h-2.5 rounded-full" 
                              style={{ width: `${user.successRate || 0}%` }}
                            ></div>
                          </div>
                          <span>{user.successRate || 0}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            <div className="mt-6 text-center">
              <a href="/leaderboard" className="text-button-primary-bg hover:underline flex items-center justify-center gap-2">
                <span>View Full Leaderboard</span>
                <FaExternalLinkAlt size={12} />
              </a>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-secondary dark:bg-background-primary text-text-primary transition-colors">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-4">
        {/* Removed empty sticky header to avoid dark band and keep colors consistent */}
        
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Sidebar */}
          <div className="w-full lg:w-1/4 space-y-4">
            {/* Chat Component */}
            <div className="card overflow-hidden" style={{ height: '600px', maxHeight: '600px' }}>
              <Suspense fallback={<div className="p-4 text-center text-text-secondary">Loading chat...</div>}>
                <CollapsibleChat sidebarMode />
              </Suspense>
            </div>
            
            {/* Actions Card */}
            <div className="card p-4">
              <h2 className="text-lg font-bold mb-4 flex items-center">
                <FaGamepad className="mr-2 text-text-accent" /> Actions
              </h2>
              <div className="space-y-2">
                <button
                  onClick={handleShowCreateModal}
                  className="btn btn-primary w-full flex items-center justify-center"
                >
                  <FaPlus className="mr-2" /> Create Challenge
                </button>
                <button
                  onClick={handleRandomChallenge}
                  className="btn btn-secondary w-full flex items-center justify-center"
                >
                  <FaRandom className="mr-2" /> Random Challenge
                </button>
                <button
                  onClick={handleHistory}
                  className="btn btn-secondary w-full flex items-center justify-center"
                >
                  <FaHistory className="mr-2" /> View History
                </button>
                <button
                  onClick={handleInviteFriendsClick}
                  className="btn btn-secondary w-full flex items-center justify-center"
                >
                  <FaUserPlus className="mr-2" /> Invite Friends
                </button>
              </div>
            </div>
            
            {/* Top Users Card */}
            <div className="card p-4">
              <h2 className="text-lg font-bold mb-4 flex items-center">
                <FaTrophy className="mr-2 text-text-accent" /> Top Users
              </h2>
              <div className="space-y-2">
                {topUsers.map((entry, index) => (
                  <div key={entry.user.id} className="flex items-center justify-between p-2 rounded-md hover:bg-background-tertiary transition-colors">
                    <div className="flex items-center">
                      <div className="size-6 rounded-full bg-background-tertiary flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </div>
                      <div className="ml-2 font-medium">{entry.user.username}</div>
                    </div>
                    <div className="text-text-accent font-medium">{entry.problemsSolved || 0} pts</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1">
            <div className="card p-4 mb-4">
              
              {/* Search and Action Buttons */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <Input
                    search
                    type="text"
                    placeholder="Search challenges..."
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                  />
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={currentUser ? handleShowCreateModal : () => toast.info('Please sign in to create challenges', { position: 'top-center' })}
                    className="btn btn-primary flex items-center gap-2 text-sm"
                    title={currentUser ? "Create a new challenge" : "Sign in to create challenges"}
                  >
                    <FaPlus /> 
                  </button>
                  
                  <button 
                    onClick={handleRandomChallenge}
                    className="btn btn-secondary flex items-center gap-2 text-sm"
                    title="Try a random challenge"
                  >
                    <FaRandom />
                  </button>
                  
                  <button 
                    onClick={currentUser ? handleInviteFriendsClick : () => toast.info('Please sign in to invite friends', { position: 'top-center' })}
                    className="btn btn-secondary flex items-center gap-2 text-sm"
                    title={currentUser ? "Invite friends" : "Sign in to invite friends"}
                  >
                    <FaUserPlus />
                  </button>
                  
                  <button 
                    onClick={currentUser ? handleHistory : () => toast.info('Please sign in to view history', { position: 'top-center' })}
                    className="btn btn-secondary flex items-center gap-2 text-sm"
                    title={currentUser ? "View challenge history" : "Sign in to view history"}
                  >
                    <FaHistory />
                  </button>
                  
                  <button
                    onClick={handleToggleFilters}
                    className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'} flex items-center justify-center`}
                    title="Filter challenges"
                  >
                    <FaFilter />
                  </button>
                  
                  <select
                    value={sortOrder}
                    onChange={handleSortChange}
                    className="select"
                    title="Sort challenges"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="starting-soon">Starting Soon</option>
                    <option value="most-participants">Most Participants</option>
                  </select>
                </div>
              </div>
              
              {/* Advanced Filters */}
              {showFilters && (
                <div className="bg-background-primary p-4 rounded-lg mb-4 animate-fadeIn border border-border-primary">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Difficulty Filter */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Difficulty</label>
                      <select
                        value={difficultyFilter}
                        onChange={handleDifficultyChange}
                        className="select w-full"
                      >
                        <option value="all">All Difficulties</option>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                        <option value="mixed">Mixed</option>
                      </select>
                    </div>
                    
                    {/* Problem Count Filter */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">Problem Count</label>
                      <select
                        value={problemCountFilter}
                        onChange={handleProblemCountChange}
                        className="select w-full"
                      >
                        <option value="all">Any Number</option>
                        <option value="1-3">1-3 Problems</option>
                        <option value="4-6">4-6 Problems</option>
                        <option value="7+">7+ Problems</option>
                      </select>
                    </div>
                    
                    {/* Clear Filters */}
                    <div className="flex items-end">
                      <button
                        onClick={handleClearFilters}
                        className="btn btn-secondary w-full"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                  
                  {/* Tags Filter */}
                  {availableTags.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-text-secondary mb-2">Tags</label>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map(tag => {
                          const handleTagClick = () => toggleTagFilter(tag);
                          return (
                            <button
                              key={tag}
                              onClick={handleTagClick}
                              className={`px-2 py-1 rounded-full text-xs font-medium ${tagFilter.includes(tag) 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-300' 
                                : 'bg-background-secondary text-text-secondary hover:bg-background-primary'}`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Status Filters */}
              <div className="flex flex-wrap gap-2 mb-4">
                <h3 className="text-sm font-medium text-text-secondary mr-2 self-center">Status:</h3>
                <button
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    statusFilter === 'active' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300 shadow-md' 
                      : 'bg-background-tertiary text-text-secondary hover:bg-background-secondary'
                  }`}
                  onClick={handleStatusFilterActive}
                >
                  Active
                </button>
                <button
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    statusFilter === 'upcoming' 
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-300 shadow-md' 
                      : 'bg-background-tertiary text-text-secondary hover:bg-background-secondary'
                  }`}
                  onClick={handleStatusFilterUpcoming}
                >
                  Upcoming
                </button>
                <button
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    statusFilter === 'completed' 
                      ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:bg-opacity-50 dark:text-gray-300 shadow-md' 
                      : 'bg-background-tertiary text-text-secondary hover:bg-background-secondary'
                  }`}
                  onClick={handleStatusFilterCompleted}
                >
                  Completed
                </button>
                <button
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    statusFilter === 'all' 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                      : 'bg-background-tertiary text-text-secondary hover:bg-background-secondary'
                  }`}
                  onClick={handleStatusFilterAll}
                >
                  Show All
                </button>
              </div>

              {/* Challenge Cards */}
              {loading ? (
                <ChallengeListSkeleton />
              ) : challenges.length === 0 ? (
                 <div className="text-center py-12 bg-background-tertiary rounded-lg">
                   <FaGamepad className="mx-auto text-4xl text-text-tertiary mb-4" />
                   <h3 className="text-xl font-medium text-text-primary mb-2">No challenges found</h3>
                   <p className="text-text-secondary mb-4">
                     {activeTab === 'my' 
                       ? "You haven't created any challenges yet." 
                       : activeTab === 'joined' 
                         ? "You haven't joined any challenges yet." 
                         : statusFilter === 'completed'
                           ? "No completed challenges found."
                           : statusFilter === 'active'
                             ? "No active challenges found."
                             : statusFilter === 'upcoming'
                               ? "No upcoming challenges found."
                               : "No challenges match your search criteria."}
                   </p>
                   {activeTab === 'my' && (
                     <button
                       onClick={handleShowCreateModal}
                       className="btn btn-primary"
                     >
                       <FaPlus className="mr-2" /> Create Your First Challenge
                     </button>
                   )}
                   {statusFilter !== 'all' && (
                     <button
                       onClick={handleStatusFilterAll}
                       className="btn btn-secondary mt-2"
                     >
                       Show All Challenges
                     </button>
                   )}
                 </div>
              ) : (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {challenges.map(challenge => {
                    const handleClick = () => handleChallengeClick(challenge._id);
                    return (
                      <div 
                        key={challenge._id} 
                        className="card p-4 hover:shadow-md transition-shadow cursor-pointer bg-background-primary"
                        onClick={handleClick}
                      >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold">{challenge.title}</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          {challenge.visibility === 'private' && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:bg-opacity-20 dark:text-purple-300 flex items-center gap-1">
                              <FaLock className="w-3 h-3" />
                              Private
                            </span>
                          )}
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            challenge.status === 'active' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300' 
                              : challenge.status === 'upcoming' 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-300' 
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:bg-opacity-50 dark:text-gray-300'
                          }`}>
                            {challenge.status === 'active' ? 'Active' : 
                             challenge.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-text-secondary mb-2 line-clamp-2">
                        {challenge.description || 'No description provided'}
                      </p>
                      
                      <div className="flex items-center text-text-tertiary text-sm mb-3">
                        <div className="flex items-center mr-4">
                          <FaUsers className="mr-1" />
                          {challenge.participants?.length || 0} participants
                        </div>
                        <div className="flex items-center">
                          <FaCode className="mr-1" />
                          {challenge.problems?.length || 0} problems
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-text-tertiary">
                          Created {new Date(challenge.createdAt).toLocaleDateString()}
                        </div>
                        <div className="flex space-x-2">
                          {/* Show reminder button for upcoming challenges */}
                          {challenge.status === 'upcoming' && (
                            <button 
                              className={`p-2 rounded-full hover:bg-background-tertiary ${
                                remindersSet[challenge._id] 
                                  ? 'text-yellow-500 hover:text-yellow-600' 
                                  : 'text-text-secondary hover:text-text-primary'
                              } transition-colors`}
                              onClick={(e) => handleReminderClick(e, challenge)}
                              title={remindersSet[challenge._id] ? "Reminder set" : "Set reminder"}
                              disabled={remindersSet[challenge._id]}
                            >
                              {remindersSet[challenge._id] ? <FaBellSlash /> : <FaBell />}
                            </button>
                          )}
                          
                          {/* Owner actions */}
                          {challenge.createdBy?._id === currentUser?._id && (
                            <>
                              <button 
                                className="p-2 rounded-full hover:bg-background-tertiary text-text-secondary hover:text-text-primary transition-colors"
                                onClick={(e) => handleEditClick(e, challenge)}
                                title="Edit challenge"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="p-2 rounded-full hover:bg-background-tertiary text-text-secondary hover:text-red-500 transition-colors"
                                onClick={(e) => handleDeleteClick(e, challenge)}
                                title="Delete challenge"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                          
                          {/* Share options */}
                          <button 
                            className="p-2 rounded-full hover:bg-background-tertiary text-text-secondary hover:text-text-primary transition-colors"
                            onClick={(e) => handleInviteClick(e, challenge)}
                            title="Invite friends"
                          >
                            <FaShare />
                          </button>
                          
                          <button 
                            className="p-2 rounded-full hover:bg-background-tertiary text-text-secondary hover:text-text-primary transition-colors"
                            onClick={handleCopyLinkClick}
                            title="Copy link"
                          >
                            <FaLink />
                          </button>
                          
                          {/* Web Share API if supported */}
                          {navigator.share && (
                            <button 
                              className="p-2 rounded-full hover:bg-background-tertiary text-text-secondary hover:text-text-primary transition-colors"
                              onClick={(e) => handleShareClick(e, challenge)}
                              title="Share"
                            >
                              <FaExternalLinkAlt />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
                
                {/* Loading More Indicator */}
                {loadingMore && (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full size-8 border-b-2 border-button-primary-bg"></div>
                    <span className="ml-3 text-text-secondary">Loading more challenges...</span>
                  </div>
                )}
                
                {/* End of Results */}
                {!hasMore && challenges.length > 0 && (
                  <div className="text-center py-8 text-text-tertiary">
                    <p>You've reached the end of the list</p>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Create Challenge Modal */}
      {showCreateChallengeModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"><div className="animate-spin rounded-full size-12 border-b-2 border-white"></div></div>}>
          <CreateChallengeModal
            onClose={() => setShowCreateChallengeModal(false)}
            onChallengeCreated={handleChallengeCreated}
          />
        </Suspense>
      )}
      
      {/* Invite Modal */}
      {selectedChallenge && showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background-primary dark:bg-background-tertiary rounded-lg shadow-xl w-full max-w-md transition-colors">
            <div className="flex items-center justify-between border-b border-border-primary p-4">
              <h2 className="text-xl font-bold text-text-primary">Invite to Challenge</h2>
              <button 
                onClick={handleCloseInviteModalClick}
                className="p-2 rounded-full hover:bg-background-secondary text-text-tertiary hover:text-text-primary transition-colors"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <div className="font-semibold text-text-primary mb-1">{selectedChallenge.title}</div>
                <div className="flex items-center bg-background-secondary rounded px-2 py-1">
                  <span className="flex-1 text-sm break-all">{generateInviteLink(selectedChallenge._id)}</span>
                  <button
                    className="ml-2 p-1 rounded hover:bg-background-tertiary"
                    onClick={copyInviteLink}
                  >
                    <FaLink />
                  </button>
                </div>
              </div>
                  <button
                className="btn btn-primary w-full mt-2"
                onClick={handleCloseInviteModalClick}
                  >
                Done
                  </button>
                </div>
                  </div>
                          </div>
      )}

      {/* Friends Connect Modal */}
      {showFriendsConnectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background-primary dark:bg-background-tertiary rounded-lg shadow-xl w-full max-w-lg transition-colors">
            <div className="flex items-center justify-between border-b border-border-primary p-4">
              <h2 className="text-xl font-bold text-text-primary">Connect with Friends</h2>
                        <button
                onClick={handleCloseFriendsModalClick}
                className="p-2 rounded-full hover:bg-background-secondary text-text-tertiary hover:text-text-primary transition-colors"
                        >
                <FaTimes />
                        </button>
                      </div>
            <div className="p-6">
              <div className="mb-4 flex gap-2">
                  <input
                    type="text"
                  placeholder="Search by username..."
                  value={usernameSearch}
                  onChange={handleUsernameSearchChange}
                  className="input flex-1"
                  onKeyDown={handleUsernameSearchKeyDown}
                />
                <button className="btn btn-primary" onClick={handleSearchUsers} disabled={searchLoading}>
                  <FaSearch />
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {searchLoading ? (
                  <div className="text-center py-4">Searching...</div>
                ) : !Array.isArray(searchResults) || searchResults.length === 0 ? (
                  <div className="text-center py-4 text-text-tertiary">No users found.</div>
                ) : (
                  <ul>
                    {searchResults.map(user => (
                      <li key={user._id} className="flex items-center justify-between py-2 border-b last:border-b-0 border-border-secondary">
                        <span className="font-medium">{user.username}</span>
                        <div className="flex gap-2 items-center">
                          {user.isFriend ? (
                            <span className="text-green-500 text-xs">Friend</span>
                          ) : user.requestSent ? (
                            <span className="text-yellow-500 text-xs">Request Sent</span>
                          ) : (
                            (() => {
                              const handleClick = () => handleInviteUser(user._id, user.isFriend);
                              return (
                                <button
                                  className="btn btn-secondary btn-xs"
                                  onClick={handleClick}
                                >
                                  {user.isFriend ? 'Invite' : 'Add Friend'}
                                </button>
                              );
                            })()
                          )}
                </div>
                      </li>
                    ))}
                  </ul>
                )}
                        </div>
                        <button
                className="btn btn-secondary w-full mt-4"
                onClick={handleCloseFriendsModalClick}
                        >
                Close
                        </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background-primary dark:bg-background-tertiary rounded-lg shadow-xl w-full max-w-md transition-colors">
            <div className="flex items-center justify-between border-b border-border-primary p-4">
              <h2 className="text-xl font-bold text-text-primary">Delete Challenge</h2>
              <button 
                onClick={handleCloseDeleteModalClick}
                className="p-2 rounded-full hover:bg-background-secondary text-text-tertiary hover:text-text-primary transition-colors"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-text-primary mb-4">
                Are you sure you want to delete the challenge "{challengeToDelete?.title}"? This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCloseDeleteModalClick}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteChallenge}
                  className="btn btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      {/* All Invite Friends, Invite by Email, Search by Username, and Share Link UI, logic, handlers, and modals have been removed from this file. */}
      
      {/* Friends Connect Modal */}
      {/* All Invite Friends, Invite by Email, Search by Username, and Share Link UI, logic, handlers, and modals have been removed from this file. */}
      </div>
    </div>
  );
};

export default Challenges;